// Training frames: small preview frames kept around each shot, packed once per
// product. Nothing here runs on the shutter path: the ring samples on a timer
// while the camera is idle, encodes happen in idle callbacks after the shot's
// own encode, and the pack is built after Confirm.
//
// Pack container, one binary blob per product:
//   "CTF1" | u32 LE header length | UTF-8 JSON manifest | JPEG bytes
// Each manifest frame's off/len index into the JPEG section.

const MAGIC = "CTF1";
const hasBitmap = typeof createImageBitmap === "function";

const TRIP_KEY = "cosmos.capture.trainRingOff";
// The trip is remembered for this tab only. Measured 10-02 on an Android 4K
// stream: grabs 9-23 ms, but a stall at lock-in tripped a day-long memory.
const defaultStore = {
  get: () => {
    try {
      return window.sessionStorage.getItem(TRIP_KEY) === "1";
    } catch {
      return false;
    }
  },
  set: () => {
    try {
      window.sessionStorage.setItem(TRIP_KEY, "1");
    } catch {
      /* the session stays off anyway */
    }
  },
};

/**
 * A ring of the last `size` preview frames, sampled every `intervalMs`.
 * start(video) / stop() / take() / hold() / release(). take() hands the bitmaps
 * to the caller (who must close them) and empties the ring. While held (a shot
 * is in flight), hidden or stopped, the ring does not sample.
 *
 * VALVE. The synchronous part of each grab (the createImageBitmap call, which
 * on some handsets reads the whole frame back) is timed. The first `warmup`
 * grabs after start() are ignored (autofocus and the lock wash stall them).
 * The ring trips when one grab takes over `maxSingleMs` or the median of the
 * last 5 exceeds `maxMedianMs`: it stops, and the trip is remembered for the tab. `force` ignores the valve and
 * any remembered trip (debug).
 */
export function createFrameRing({
  size = 4,
  intervalMs = 500,
  edge = 256,
  maxMedianMs = 40,
  maxSingleMs = 250,
  warmup = 3,
  force = false,
  store = defaultStore,
} = {}) {
  let ring = [];
  let timer = null;
  let video = null;
  let held = 0;
  let grabbing = false;
  let warm = 0;
  let recent = [];
  let tripped = !force && store.get();
  // Why a pack has (or lacks) pre-tap frames, reported in its manifest.
  const stats = { remembered: tripped, forced: force, tripped, grabs: 0, kept: 0, heldTicks: 0, syncMs: [] };

  function trip() {
    tripped = true;
    stats.tripped = true;
    store.set();
    clearInterval(timer);
    timer = null;
    ring.forEach((r) => r.bmp.close?.());
    ring = [];
  }

  async function tick() {
    if (held > 0) stats.heldTicks += 1;
    if (grabbing || held > 0 || !video || document.visibilityState !== "visible") return;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh || video.paused) return;
    grabbing = true;
    try {
      const t = Date.now();
      const resizeWidth = Math.max(1, Math.round(vw * Math.min(1, edge / Math.max(vw, vh))));
      const g = grab(video, resizeWidth, vh / vw);
      const bmp = await g.bmp;
      stats.grabs += 1;
      stats.syncMs = [...stats.syncMs, Math.round(g.syncMs() * 10) / 10].slice(-12);
      if (warm < warmup) warm += 1;
      else if (!force) {
        recent = [...recent, g.syncMs()].slice(-5);
        const med = [...recent].sort((x, y) => x - y)[recent.length >> 1];
        if (g.syncMs() > maxSingleMs || (recent.length === 5 && med > maxMedianMs)) {
          bmp?.close?.();
          return trip();
        }
      }
      if (!bmp) return;
      // Stopped, held or taken while the grab was in flight: not wanted.
      if (!timer || held > 0) return bmp.close?.();
      ring.push({ bmp, t });
      stats.kept += 1;
      while (ring.length > size) ring.shift().bmp.close?.();
    } catch {
      /* a missed sample is fine */
    } finally {
      grabbing = false;
    }
  }

  return {
    start(v) {
      video = v;
      if (!timer && !tripped) {
        warm = 0;
        recent = [];
        timer = setInterval(tick, intervalMs);
      }
    },
    stop() {
      clearInterval(timer);
      timer = null;
      video = null;
      ring.forEach((r) => r.bmp.close?.());
      ring = [];
    },
    /** Move the current frames out: [{ bmp, t }] oldest first. */
    take() {
      const out = ring;
      ring = [];
      return out;
    },
    hold() {
      held += 1;
    },
    release() {
      held = Math.max(0, held - 1);
    },
    stats: () => ({ ...stats, syncMs: [...stats.syncMs] }),
  };
}

/** One preview grab: { bmp: Promise, syncMs() } where syncMs is the time the call itself blocked. */
function grab(video, resizeWidth, aspect) {
  const t0 = performance.now();
  let sync = 0;
  const bmp = (async () => {
    try {
      const p = createImageBitmap(video, { resizeWidth });
      sync = performance.now() - t0;
      return await p;
    } catch {
      // No createImageBitmap(video): draw onto a small canvas.
      const c = document.createElement("canvas");
      c.width = resizeWidth;
      c.height = Math.max(1, Math.round(resizeWidth * aspect));
      c.getContext("2d")?.drawImage(video, 0, 0, c.width, c.height);
      sync = performance.now() - t0;
      if (!hasBitmap) return null;
      try {
        return await createImageBitmap(c);
      } catch {
        return null;
      }
    }
  })();
  return { bmp, syncMs: () => sync };
}

/** Run `fn` when the page is idle (or after 50 ms where there is no idle callback). */
export function whenIdle(fn) {
  return new Promise((resolve) => {
    const run = () => resolve(fn());
    if (typeof requestIdleCallback === "function") requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 50);
  });
}

let encodeCanvas = null;

/**
 * JPEG q0.7 blobs of the bitmaps, one reused canvas, one frame per idle slot.
 * Every bitmap is closed. A frame that fails to encode comes back null.
 * @param {Array<{bmp: ImageBitmap|HTMLCanvasElement}>} items
 * @returns {Promise<Array<{blob: Blob, w: number, h: number}|null>>}
 */
export async function encodeFrames(items) {
  const out = [];
  try {
    for (const { bmp } of items) {
      out.push(await whenIdle(() => encodeOne(bmp)));
    }
  } finally {
    items.forEach((i) => i.bmp.close?.());
    if (encodeCanvas) {
      encodeCanvas.width = 0;
      encodeCanvas.height = 0;
    }
  }
  return out;
}

async function encodeOne(bmp) {
  try {
    encodeCanvas = encodeCanvas || document.createElement("canvas");
    encodeCanvas.width = bmp.width;
    encodeCanvas.height = bmp.height;
    encodeCanvas.getContext("2d")?.drawImage(bmp, 0, 0);
    const blob = await new Promise((r) => encodeCanvas.toBlob(r, "image/jpeg", 0.7));
    return blob ? { blob, w: bmp.width, h: bmp.height } : null;
  } catch {
    return null;
  }
}

/** The chosen frame: the shot's 320px thumbnail JPEG, decoded and re-encoded at `edge` px when idle. */
export async function encodeChosen(thumb, edge = 256) {
  const bmp = await whenIdle(async () => {
    try {
      const probe = await createImageBitmap(thumb);
      const scale = Math.min(1, edge / Math.max(probe.width, probe.height));
      if (scale >= 1) return probe;
      const small = await createImageBitmap(probe, { resizeWidth: Math.max(1, Math.round(probe.width * scale)) });
      probe.close();
      return small;
    } catch {
      return null;
    }
  });
  return bmp ? (await encodeFrames([{ bmp }]))[0] : null;
}

/**
 * One product's recorder. Shots are added at the tap with the ring's frames,
 * finished once the shot's own encode resolves, and marked deleted when the
 * merchant removes them. finalize() resolves to a pack, or null.
 */
export function createProductRecorder({ shop, getVideoSize, ring }) {
  const shots = new Map();
  let seq = 0;
  let startedAt = 0;
  let closed = false;

  const release = (s) => {
    if (s.held) ring.release();
    s.held = false;
  };

  return {
    /** At the tap, synchronously: pause the ring and take its frames. */
    addShot(id) {
      if (closed) return;
      const tapAt = Date.now();
      startedAt = startedAt || tapAt;
      ring.hold();
      const video = getVideoSize?.() || null;
      shots.set(id, { id, i: seq++, tapAt, video, kept: true, q: null, held: true, frames: ring.take(), done: null, encoded: [] });
    },
    /** The shot's own encode resolved (`blob` is its thumbnail JPEG) or it was removed first (null). Encodes when idle. */
    finishShot(id, blob, q) {
      const s = shots.get(id);
      if (!s || s.done) return;
      s.q = q || null;
      release(s);
      const frames = s.frames;
      s.frames = [];
      s.done = (async () => {
        const dts = frames.map((f) => s.tapAt - f.t);
        const enc = await encodeFrames(frames);
        if (closed) return;
        s.encoded = enc.map((e, k) => (e ? { dt: dts[k], ...e } : null)).filter(Boolean);
        const chosen = blob ? await encodeChosen(blob) : null;
        if (chosen && !closed) s.encoded.push({ dt: 0, ...chosen });
      })();
    },
    markDeleted(id) {
      const s = shots.get(id);
      if (s) s.kept = false;
    },
    /** A shot that never produced a photograph (capture failed): forget it. */
    dropShot(id) {
      const s = shots.get(id);
      if (!s) return;
      release(s);
      s.frames.forEach((f) => f.bmp.close?.());
      shots.delete(id);
    },
    /** Close everything; the recorder is dead afterwards. */
    discard() {
      closed = true;
      shots.forEach((s) => {
        release(s);
        s.frames.forEach((f) => f.bmp.close?.());
      });
      shots.clear();
    },
    /**
     * @param {{uploadId: string, photoIndexById: Map<string, number>}} args
     * @returns {Promise<{blob: Blob, uploadId: string, size: number}|null>}
     */
    async finalize({ uploadId, photoIndexById }) {
      if (closed) return null;
      const list = [...shots.values()];
      list.forEach((s) => release(s));
      await Promise.all(list.map((s) => s.done));
      // A shot that never finished still holds bitmaps.
      list.forEach((s) => s.frames.forEach((f) => f.bmp.close?.()));
      closed = true;
      const withFrames = list.filter((s) => s.encoded.length > 0);
      if (withFrames.length === 0) return null;
      const blobs = [];
      const manifest = {
        v: 1,
        uploadId,
        shop,
        ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
        screen: typeof screen !== "undefined" ? { w: screen.width, h: screen.height, dpr: window.devicePixelRatio || 1 } : null,
        video: list[0]?.video || null,
        startedAt,
        ring: ring.stats?.() || null,
        shots: withFrames.map((s) => {
          const kept = s.kept && photoIndexById.has(s.id);
          return {
            i: s.i,
            tapAt: s.tapAt,
            kept,
            q: s.q,
            photoIndex: kept ? photoIndexById.get(s.id) : null,
            frames: s.encoded.map((f) => {
              blobs.push(f.blob);
              return { dt: f.dt, w: f.w, h: f.h };
            }),
          };
        }),
      };
      const blob = await buildPack(manifest, blobs);
      return { blob, uploadId, size: blob.size };
    },
  };
}

/**
 * The container. `blobs` follows the manifest's frames in order (shot by shot);
 * off/len are filled in here.
 * @returns {Promise<Blob>}
 */
export async function buildPack(manifest, blobs) {
  let off = 0;
  let k = 0;
  const m = { ...manifest, shots: manifest.shots.map((s) => ({ ...s, frames: s.frames.map((f) => {
    const len = blobs[k++].size;
    const out = { ...f, off, len };
    off += len;
    return out;
  }) })) };
  const head = new TextEncoder().encode(JSON.stringify(m));
  const prefix = new Uint8Array(8);
  prefix.set(new TextEncoder().encode(MAGIC), 0);
  new DataView(prefix.buffer).setUint32(4, head.length, true);
  return new Blob([prefix, head, ...blobs], { type: "application/octet-stream" });
}

/** Parse a pack: { manifest, jpegs: Uint8Array[] in manifest order }. Throws on a bad container. */
export function parsePack(buf) {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  if (bytes.length < 8 || new TextDecoder().decode(bytes.subarray(0, 4)) !== MAGIC) {
    throw new Error("bad_magic");
  }
  const hl = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(4, true);
  if (8 + hl > bytes.length) throw new Error("bad_header");
  const manifest = JSON.parse(new TextDecoder().decode(bytes.subarray(8, 8 + hl)));
  const base = 8 + hl;
  const jpegs = [];
  for (const s of manifest.shots) {
    for (const f of s.frames) {
      if (base + f.off + f.len > bytes.length) throw new Error("bad_frame");
      jpegs.push(bytes.subarray(base + f.off, base + f.off + f.len));
    }
  }
  return { manifest, jpegs };
}
